import { Box, Divider, Grid, HStack, Link, Popover, PopoverBody, PopoverContent, PopoverTrigger, Progress, Tab, Table, TableCaption, TableContainer, TabList, TabPanel, TabPanels, Tabs, Tbody, Td, Th, Thead, Tooltip, Tr, useDisclosure, VStack } from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import moment from 'moment';
import numeral from 'numeral';
import React, { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { IconType } from 'react-icons';
import { MdOutlineMarkChatUnread, MdVideoSettings } from 'react-icons/md';
import styled from 'styled-components';
import { CardUpdateModal } from '.';
import { PermissionsEnum } from '../../../utils';
import { EXTENSION_DISPLAY_NAMES } from '../../../utils/extensions';
import { DAMFeaturesEnum, FeatureNameMapping, OtherFeaturesEnum } from '../../../utils/features';
import type {
    ContractInfo as ContractInfoInterface, SubscriptionDetails
} from '../../interfaces';
import { UserContext } from '../../providers';
import PermissionGuard from '../PermissionGuard';

const Wrapper = styled.div`
    width: 100%;

    .brand-logo {
        // border: solid 0.5px var(--chakra-colors-gray-200);
        border-radius: 2px;
    }

    .feature-icon {
        width: 24px;
        height: 24px;
    }

    .usage-card {
        &.disabled {
            opacity: 0.8;
            cursor: not-allowed;
        }
    }
`

export const SubscriptionDetals = ({ subscription }: { subscription: SubscriptionDetails }): React.ReactElement => {
    return (
        <Wrapper>
            <SubscriptionProvider subscription={subscription}>
                <VStack spacing={3} alignItems="flex-start" w='full'>
                    <Icon />
                    <PlanName />
                    <MonthlyFixedCost />
                    <PaymentMethod />
                    <Divider borderColor="appPaleBlue" />
                </VStack>
                <PlanDetails />
            </SubscriptionProvider>
        </Wrapper>
    )
}

const PlanDetails = (): React.ReactElement => {
    const { subscription } = useSubscription();
    const [tabIndex, setTabIndex] = React.useState(0);

    // A tab of the subscription details
    return (
        <Tabs
            variant="soft-rounded"
            size="sm"
            colorScheme="appBlue"
            marginTop={"4"}
            onChange={(index) => setTabIndex(index)}
        >
            <TabList>
                <Tab>Inclusions and overage</Tab>
                <Tab>Features included</Tab>
                <Tab>Plan limits</Tab>
            </TabList>

            {(tabIndex === 0 && subscription.contractInfo.hasContract) &&
                <ContractInfo />
            }

            <TabPanels bg="#fbfcff" marginTop={4}>
                <TabPanel>
                    <InclusionAndOverage />
                </TabPanel>
                <TabPanel>
                    <FeaturesInclusion />
                </TabPanel>
                <TabPanel>
                    <PlanLimits />
                </TabPanel>
            </TabPanels>
        </Tabs>
    )
}

const Icon = (): React.ReactElement => {
    return (
        <Box
            background="appPaleGreyThree"
            borderRadius="full"
            padding={2}
        >
            <img src="https://ik.imagekit.io/ikmedia/star-sub.svg?updatedAt=1719200321494"></img>
        </Box>
    )
}

const MonthlyFixedCost = (): React.ReactElement => {
    const { subscription } = useSubscription();

    const monthlyCost = subscription.fixedCost;

    if (!monthlyCost) {

        <Box>
            <NextPayment />
        </Box>
    }

    return (
        <HStack divider={<>&nbsp;&#x2022;&nbsp;</>}>
            {monthlyCost &&
                <Box>
                    Base cost <span style={{ fontWeight: "700" }}>{monthlyCost}</span>
                </Box>
            }
            <Box>
                <NextPayment />
            </Box>
        </HStack>
    )
}

const PaymentMethod = (): React.ReactElement | null => {
    const { subscription } = useSubscription();
    const { t } = useTranslation()

    const isAutomatic = subscription.collectionMethod.method === 'automatic' ? true : false;

    if (isAutomatic && subscription.collectionMethod.cardDetails) {
        return (
            <HStack spacing="10px" justifyItems={"flex-start"}>
                <Box justifySelf={"start"} width={"32px"}>
                    <CardBrandImage brand={subscription.collectionMethod.cardDetails.brand} />
                </Box>
                <Box css={{
                    "&:first-letter": {
                        textTransform: "uppercase",
                    },
                }} >
                    {subscription.collectionMethod.cardDetails?.brand} {subscription.collectionMethod.cardDetails?.last4} {t('expires', 'expires')} on {subscription.collectionMethod.cardDetails?.exp_month}/{subscription.collectionMethod.cardDetails?.exp_year}
                </Box>
                <Box>
                    <CardDetailsPopup ctaLabel={t('update_card_details', 'Update card details')} />
                </Box>
            </HStack>
        )
    } else if (isAutomatic) {
        return (
            <HStack>
                <Box>
                    <CardDetailsPopup ctaLabel={t('enter_card_details', 'Enter card details')} />
                </Box>
            </HStack>
        )
    }

    return (
        <HStack>
            <Box>
                <CardDetailsPopup ctaLabel={t('enter_card_details', 'Enter card details')} />
            </Box>
        </HStack>
    )
}

const CardDetailsPopup = ({
    ctaLabel
}, {
    ctaLabel: string
}): React.ReactElement | null => {
    const { user } = useContext(UserContext)
    if (!user) {
        return null
    }
    const stripePromise = loadStripe(user.stripePublishKey)
    const {
        isOpen: isOpenCardUpdate,
        onClose: onCloseCardUpdate,
        onOpen: onOpenCardUpdate,
    } = useDisclosure()

    return (
        <Box>
            <Link onClick={onOpenCardUpdate}>
                {ctaLabel ?? "Enter card details"}
            </Link>
            <PermissionGuard
                permission={PermissionsEnum.SELF_BILLING_VIEW}
            >
                {
                    (hasPermission: boolean) =>
                        hasPermission ? (
                            <Elements stripe={stripePromise}>
                                <CardUpdateModal
                                    ctaLabel={ctaLabel}
                                    isOpen={isOpenCardUpdate}
                                    onClose={onCloseCardUpdate}
                                    user={user}
                                />
                            </Elements>
                        ) : <></>
                }
            </PermissionGuard>
        </Box>
    )
}

const PaymentMode = (): React.ReactElement => {
    const { subscription } = useSubscription();
    const { t } = useTranslation()
    const isAutomatic = subscription.collectionMethod.method === 'automatic';
    if (isAutomatic) {
        return (
            <>
                {t('automatic', 'Automatic')}
            </>
        )
    } else {
        return (
            <>
                {t('manual', 'Manual')}
            </>
        )
    }
}

const NextPayment = (): React.ReactElement => {
    return (
        <>
            Next payment on {moment().add(1, 'month').startOf('month').format('D MMM YYYY')} (<PaymentMode />)
        </>
    )
}

const PlanName = (): React.ReactElement => {
    const { subscription } = useSubscription();
    const { user } = useContext(UserContext);
    const planName = user?.pricingPlan?.planName ?? "Premium plan";
    const hasContract = subscription.contractInfo.hasContract;
    return (
        <Box fontSize={18} fontWeight="semibold">
            {hasContract || !subscription.isStandardPlan ? "Custom pricing plan" : planName}
        </Box>
    )
}

const CardBrandImage = ({
    brand,
}: { brand: string }): React.ReactElement | null => {
    // Swticth case for different card brands
    switch (brand) {
        case "visa":
            return (
                <img className="brand-logo" src="https://ik.imagekit.io/demo/visa-3.svg"></img>
            )
        case "mastercard":
            return (
                <img className="brand-logo" src="https://ik.imagekit.io/demo/mastercard-full.svg?v=1"></img>
            )
        case "amex":
        case "american express":
            return (
                <img className="brand-logo" src="https://ik.imagekit.io/demo/amex(1).svg"></img>
            )
        case "diners":
        case "diners club":
            return (
                <img className="brand-logo" src="https://ik.imagekit.io/demo/diners-club-international.svg?v=1"></img>
            )
        default:
            return null
    }
}

const ExtensionInfo = () => {
    const { subscription } = useSubscription();
    const { extension } = subscription;
    const { rateCard } = extension;
    return (
        <Popover trigger="hover">
            <PopoverTrigger>
                <img src="https://ik.imagekit.io/ikmedia/info.svg" />
            </PopoverTrigger>
            <PopoverContent w={"100%"}>
                <PopoverBody p={"12px"}>
                    <TableContainer>
                        <Table variant='simple' size='sm'>
                            <TableCaption>
                                Extension rate for different operations
                            </TableCaption>
                            <Thead>
                                <Tr>
                                    <Th>Extension</Th>
                                    <Th>Units per operation</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {Object.keys(rateCard).map((key: any) => {
                                    return (
                                        <Tr>
                                            <Td>{EXTENSION_DISPLAY_NAMES[key]}</Td>
                                            <Td>{rateCard[key]}</Td>
                                        </Tr>
                                    )
                                })}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    )
}

const InclusionAndOverage = (): React.ReactElement => {
    const { subscription } = useSubscription();
    const { storage, bandwidth, request, userPrice, CNAMEPrice, originalCacheStoragePrice, extension, video, purgeCacheAPIPrice } = subscription;
    const items = [bandwidth, storage, video, extension, userPrice, purgeCacheAPIPrice, CNAMEPrice, originalCacheStoragePrice, request];
    return (
        <Grid
            templateColumns="repeat(2, 1fr)"
            gap={4}
        >
            {items.map((item: any) => {
                if ((item.inclusion === "" && item.overage === "")) return null
                const isExenstionItem = item.label.toLowerCase().includes("extension units");
                return (
                    <VStack alignItems={"flex-start"}>
                        <Box color={"appSlate"} fontSize={13} position={"relative"}>
                            <HStack>
                                <Box>{item.label}</Box>
                                {isExenstionItem && <ExtensionInfo />}
                            </HStack>
                        </Box>
                        <Box marginTop={"0px !important"}>
                            {(!item.inclusion || item.inclusion === "") &&
                                <Tooltip label='Inclusions' whiteSpace={"nowrap"}>
                                    No inclusions
                                </Tooltip>
                            }
                            {(item.inclusion && item.inclusion !== "") &&
                                <Tooltip label='Inclusions' whiteSpace={"nowrap"}>
                                    {item.inclusion}
                                </Tooltip>
                            }
                            {(item.overage && item.overage !== "") &&
                                <>
                                    <Tooltip label='Overage' whiteSpace={"nowrap"}>
                                        <Box css={{
                                            "&:first-letter": {
                                                fontFamily: "sans-serif"
                                            },
                                        }}>
                                            {item.overage}
                                        </Box>
                                    </Tooltip>
                                </>
                            }
                        </Box>
                    </VStack>
                )
            })}
        </Grid>
    )
}

const FeaturesInclusion = (): React.ReactElement => {
    const { user } = useContext(UserContext);
    const { subscription } = useSubscription();
    const hasContract = subscription.contractInfo.hasContract;

    return (
        <>
            <Box color={"appSlate"} fontSize={13} marginBottom={4}>
                Digital Asset Management
            </Box>
            <Grid
                templateColumns="repeat(2, 1fr)"
                gap={4}
            >
                {DAMFeaturesEnum.map((featureKey: string) => {
                    if (user?.pricingPlan?.featuresList.includes(featureKey)) {
                        return (
                            <HStack>
                                <Box>
                                    <img className="feature-icon" src="https://ik.imagekit.io/ikmedia/checkmark(1).svg" />
                                </Box>
                                <Box>
                                    {FeatureNameMapping[featureKey]}
                                </Box>
                            </HStack>

                        )
                    } else {
                        return (
                            <HStack>
                                <Box>
                                    <img className="feature-icon" src="https://ikmedia.imagekit.io/Cross.svg?updatedAt=1719296144777" />
                                </Box>
                                <Box>
                                    {FeatureNameMapping[featureKey]}
                                </Box>
                            </HStack>
                        )
                    }
                })}
            </Grid>

            {user?.pricingPlan?.planType !== "DAM_ONLY" &&
                <>
                    <Box color={"appSlate"} fontSize={13} marginBottom={4} marginTop={8}>
                        Media processing and general
                    </Box>
                    <Grid
                        templateColumns="repeat(2, 1fr)"
                        gap={4}
                    >
                        {OtherFeaturesEnum.map((featureKey: string) => {
                            if (user?.pricingPlan?.featuresList.includes(featureKey)) {
                                return (
                                    <HStack>
                                        <Box>
                                            <img className="feature-icon" src="https://ik.imagekit.io/ikmedia/checkmark(1).svg" />
                                        </Box>
                                        <Box>
                                            {FeatureNameMapping[featureKey]}
                                        </Box>
                                    </HStack>

                                )
                            } else {
                                return (
                                    <HStack>
                                        <Box>
                                            <img className="feature-icon" src="https://ikmedia.imagekit.io/Cross.svg?updatedAt=1719296144777" />
                                        </Box>
                                        <Box>
                                            {FeatureNameMapping[featureKey]}
                                        </Box>
                                    </HStack>
                                )
                            }
                        })
                            .concat(["Image transformation and optimization", "Raw file delivery (non-image, non-video files)", "Integrated CDN with HTTPS"].map((featureKey: string) => {
                                return (
                                    <HStack>
                                        <Box>
                                            <img className="feature-icon" src="https://ik.imagekit.io/ikmedia/checkmark(1).svg" />
                                        </Box>
                                        <Box>
                                            {featureKey}
                                        </Box>
                                    </HStack>
                                )
                            }))}
                    </Grid>
                </>
            }


            <Box color={"appSlate"} fontSize={13} marginBottom={4} marginTop={8}>
                AI-powered capabilities
            </Box>
            <Grid
                templateColumns="repeat(2, 1fr)"
                gap={4}
            >
                {Object.values(EXTENSION_DISPLAY_NAMES).map((featureKey: string) => {
                    return (
                        <HStack>
                            <Box>
                                <img className="feature-icon" src="https://ik.imagekit.io/ikmedia/checkmark(1).svg" />
                            </Box>
                            <Box>
                                {featureKey}
                            </Box>
                        </HStack>
                    )
                })}
            </Grid>

            <Box color={"appSlate"} fontSize={13} marginBottom={4} marginTop={8}>
                Support
            </Box>

            <Grid
                templateColumns="repeat(2, 1fr)"
                gap={4}
            >

                {hasContract &&
                    <>
                        <HStack>
                            <Box>
                                <img className="feature-icon" src="https://ik.imagekit.io/ikmedia/checkmark(1).svg" />
                            </Box>
                            <Box>
                                Priority support with Docs, Email, Chat, Phone, and Slack

                            </Box>
                        </HStack>
                        <HStack>
                            <Box>
                                <img className="feature-icon" src="https://ik.imagekit.io/ikmedia/checkmark(1).svg" />
                            </Box>
                            <Box>
                                Custom response time SLA
                            </Box>
                        </HStack>
                    </>
                }

                {!hasContract &&
                    <>
                        <HStack>
                            <Box>
                                <img className="feature-icon" src="https://ik.imagekit.io/ikmedia/checkmark(1).svg" />
                            </Box>
                            <Box>
                                Docs and Email
                            </Box>
                        </HStack>
                    </>
                }
            </Grid>
        </>
    )
}

const PlanLimits = (): React.ReactElement => {
    const { subscription } = useSubscription();
    const { user } = useContext(UserContext);

    const limits = [
        {
            label: "Users",
            limit: () => {
                if (user?.pricingPlan.limits.fixed.users === -1) {
                    return "Unlimited"
                } else {
                    return user?.pricingPlan.limits.fixed.users
                }
            },
        },
        {
            label: "External origins",
            limit: () => {
                if (user?.pricingPlan.limits.fixed.origins === -1) {
                    return "Unlimited"
                } else {
                    return user?.pricingPlan.limits.fixed.origins
                }
            }
        },
        {
            label: "URL endpoints",
            limit: () => {
                if (user?.pricingPlan.limits.fixed.endpoints === -1) {
                    return "Unlimited"
                } else {
                    return user?.pricingPlan.limits.fixed.endpoints
                }
            }
        },
        {
            label: "Max image file size for processing",
            limit: () => {
                return numeral(user?.pricingPlan.limits.fixed.input.size.image).format('0 ib').replace("iB", "B")
            }
        },
        {
            label: "Max image megapixels for processing",
            limit: () => {
                let formatter = Intl.NumberFormat('en', { notation: 'compact' });
                return `${formatter.format(user?.pricingPlan.limits.fixed.input.resolution.image || 0)}P`
            }
        },
        {
            label: "Max video file size for processing",
            limit: () => {
                return numeral(user?.pricingPlan.limits.fixed.input.size.video).format('0 ib').replace("iB", "B")
            }
        },
        {
            label: "Max raw file size for delivery",
            limit: () => {
                return numeral(user?.pricingPlan.limits.fixed.input.size.image).format('0 ib').replace("iB", "B")
            }
        },
        {
            label: "Max image type file upload size",
            limit: () => {
                return numeral(user?.pricingPlan.limits.fixed.uploadSize.image).format('0 ib').replace("iB", "B")
            }
        },
        {
            label: "Max video type file upload size",
            limit: () => {
                return numeral(user?.pricingPlan.limits.fixed.uploadSize.video).format('0 ib').replace("iB", "B")
            }
        },
        {
            label: "Max audio file upload size",
            limit: () => {
                return numeral(user?.pricingPlan.limits.fixed.uploadSize.audio).format('0 ib').replace("iB", "B")
            }
        },
        {
            label: "Max raw file upload size",
            limit: () => {
                return numeral(user?.pricingPlan.limits.fixed.uploadSize.raw).format('0 ib').replace("iB", "B")
            }
        },
        {
            label: "Performance center URLs",
            limit: () => {
                return user?.pricingPlan.limits.fixed.performanceCenter.urls !== -1 ? user?.pricingPlan.limits.fixed.performanceCenter.urls : "Unlimited"
            }
        },
        {
            label: "Custom CNAMEs",
            limit: () => {
                return user?.pricingPlan.limits.fixed.customCnames !== -1 ? user?.pricingPlan.limits.fixed.customCnames : "Unlimited"
            }
        },
        {
            label: "Max public links",
            limit: () => {
                return user?.pricingPlan.limits.fixed.publicLinks !== -1 ? user?.pricingPlan.limits.fixed.publicLinks : "Unlimited"
            }
        },
        {
            label: "Max purge cache requests",
            limit: () => {
                return user?.pricingPlan.limits.rollingMontly.purgeCache !== -1 ? `${user?.pricingPlan.limits.rollingMontly.purgeCache} requests per month` : "Unlimited"
            }
        }
    ]
    return (
        <>

            <Grid
                templateColumns="repeat(2, 1fr)"
                gap={4}
            >
                {limits.map((limit: any) => {
                    return (
                        <VStack alignItems={"flex-start"}>
                            <Box color={"appSlate"} fontSize={13}>
                                {limit.label}
                            </Box>
                            <Box marginTop={"0px !important"}>
                                {limit.limit()}
                            </Box>
                        </VStack>
                    )
                })}
            </Grid>

        </>

    )
}

const ContractInfo = (): React.ReactElement => {
    const { subscription } = useSubscription();

    const contractInfo = subscription.contractInfo as ContractInfoInterface;

    return (
        <Box background="appPaleGreyThree"
            borderRadius="8px"
            marginTop={4}
            padding={4}>
            <VStack spacing={4}>
                <HStack justifyContent={"space-between"} width={"full"}>
                    <Box color={"appSlate"} fontSize={13} fontWeight={"500"}>Contract inclusions</Box>
                    <Box color={"appSlate"} fontSize={13}>
                        {contractInfo.contractMonths} months contract • Effective {moment(contractInfo.effectiveContractStartDate).format('D MMM YYYY')}
                    </Box>
                </HStack>
                <HStack width={"full"} spacing={4}>
                    <UsageCard
                        IconComponent={MdOutlineMarkChatUnread}
                        label={subscription.bandwidth.label}
                        used={Math.round(contractInfo.bandwidthConsumedInGB || 0)}
                        total={Math.round(contractInfo.contractBandwidthQuotaInGB || 0)}
                        unit={"bandwidth"} />
                    <UsageCard
                        IconComponent={MdVideoSettings}
                        label={subscription.video.label}
                        used={Math.round(contractInfo.videoProcessingUnitsConsumedInCount || 0)}
                        total={Math.round(contractInfo.contractVideoProcessingUnitsQuotaInCount || 0)}
                        unit={"number"} />
                </HStack>
            </VStack>
        </Box>
    )
}

const UsageCard = ({
    IconComponent,
    label,
    used,
    total,
    unit
}: {
    IconComponent: IconType
    label: string
    used: number,
    total: number,
    unit: string
}): React.ReactElement => {
    const disabled = total === 0;
    return (
        <Box className={`usage-card ${disabled ? "disabled" : ""}`} backgroundColor="white" padding={4} borderRadius={"8px"} width={"50%"} minHeight={"120px"}>
            <VStack justifyContent={"flex-start"} align="left" >
                <IconComponent size="1.5rem" color={"#2c93fa"} />

                <Box color={"#4a5568"} fontSize={13}>
                    {label}
                </Box>

                {disabled ? (
                    <Box color={"#4a5568"} fontSize={13}>
                        Only monthly inclusion
                    </Box>
                ) : (
                    <>
                        <Box color={"#4a5568"} fontSize={13}>
                            <Box fontWeight={"semibold"} as={"span"} color={"#1a202c"} fontSize={"16px"}>
                                {used}/{total}
                                {unit === "bandwidth" ? " GB" : ""}
                            </Box>
                            <Box color={"appCoolGrey"} as={"span"}>
                                &nbsp;utilized
                            </Box>
                        </Box>
                        <Progress height='4px' value={used} min={0} max={total} borderRadius={"100px"} />
                    </>
                )}
            </VStack>
        </Box>

    )
}

const SubscriptionContext = createContext<{ subscription: SubscriptionDetails } | undefined>(undefined);

// Custom hook for accessing the subscription context
const useSubscription = (): { subscription: SubscriptionDetails } => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
};

// Create a provider component
const SubscriptionProvider: React.FC<{ subscription: SubscriptionDetails }> = ({ subscription, children }) => {
    return (
        <SubscriptionContext.Provider value={{ subscription }}>
            {children}
        </SubscriptionContext.Provider>
    );
};